import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { IKO_KEYS, LovableTabs } from "@/design-system";
import { useAuth } from "@/components/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Camera, Ticket, Check, Bookmark } from "lucide-react";
import { useIsDesktop } from "@/lib/useMediaQuery";
import {
  IkoChipRow, PosterViewer, useWidthBand,
  FeatureCard, CompactCard, VerticalCard, bucketEvent, pickPillLabel, nextStartAt,
  formatPriceLine, ikoLabel, ikoTitleVar, metaLine, ikoPanelBg, isOnNow, PartOfLine,
} from "./feedCard";
import { PostsGrid, ReviewsFeed } from "./FeedTabs";
import { trackTicketClick } from "@/lib/analytics";
import { useChains, ChainFeedCard } from "@/components/member/ChainCards";

const API = process.env.REACT_APP_BACKEND_URL;
const publicClient = axios.create({ baseURL: `${API}/api` });

/**
 * Vania Msg 210 (30 Sep 2026) — new For You order:
 *   1. today's pick (featured)
 *   2. usikose (don't miss it)      — up to 6 biggest upcoming events beyond today
 *   3. today                        — rest of today's events
 *   4. you'd probably love this     — always shown; personalised, iko-mixed fallback
 *   5. paid partnership             — hidden when no placements
 *   6. insiders' favourites         — locked teaser for guests
 *   7. been there?                  — last 3 months
 *
 * "this weekend" and "this week" are REMOVED from For You.
 * Empty sections are hidden (never show an empty heading or "nothing" note).
 */
const IKO_MAX_IN_MIX = 2;         // max cards per iko in a mixed rail
const USIKOSE_LIMIT = 6;
const YPLT_LIMIT   = 6;

// A "biggest event" heuristic in the absence of published scores: prefer higher
// going_count, then soonest. Ready to switch to score once desks publish them.
function bigScore(ev) {
  return (ev.going_count || 0) + (ev.is_day_pick ? 25 : 0);
}
function mixByIko(pool, limit) {
  const out = [];
  const per = { sherehe: 0, polite: 0, michezo: 0, kazi: 0 };
  for (const e of pool) {
    if ((per[e.iko] || 0) >= IKO_MAX_IN_MIX && out.length < limit) continue;
    out.push(e);
    per[e.iko] = (per[e.iko] || 0) + 1;
    if (out.length >= limit) break;
  }
  return out;
}

export default function ForYou() {
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  // WW-146.h · approved items outside Kenya, shown to everyone.
  const [safariRows, setSafariRows] = useState([]);
  useEffect(() => {
    publicClient.get("/public/events/safari").then((r) => setSafariRows(r.data.events || [])).catch(() => setSafariRows([]));
  }, []);
  const [prefs, setPrefs] = useState({});
  const [prefsSet, setPrefsSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iko, setIko] = useState("all");
  const [viewer, setViewer] = useState(null);
  // Iter 28 · Msg 231 — For You tabs.
  const [tab, setTab] = useState("events");
  // Wave 15.11 (Vania 4 Oct) · scroll-edge fade only shows after the member
  // has actually scrolled. At rest the first card sits a crisp 16px below
  // the chip row with no gradient overlapping it.
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 0);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  const { band, cols, wide } = useWidthBand();
  const { user } = useAuth();
  const nav = useNavigate();
  const isPaid = !!user && ["trialing", "founding", "paid", "comped"].includes(user.membership_state);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      // Wave 15.13b (Vania 5 Oct) · For You stays in watu wako's home area
      // (Nairobi county + 11 nearby towns, D59). Backend honours ?home_only=true.
      publicClient.get("/public/events?home_only=true"),
      publicClient.get("/public/events?time=past&past_days=90&limit=60&home_only=true").catch(() => ({ data: { events: [] } })),
      user ? publicClient.get("/me/preferences", { withCredentials: true }).catch(() => ({ data: { preferences: {}, preferences_set_at: null } })) : Promise.resolve({ data: { preferences: {}, preferences_set_at: null } }),
    ]).then(([a, b, p]) => {
      setEvents(a.data.events || []);
      setPastEvents(b.data.events || []);
      const pref = p.data.preferences || {};
      setPrefs(pref);
      // Vania Msg 216 §1c — "no answers yet" means 0 filled fields, whether or
      // not the user skipped. Card stays visible until they actually answer.
      const answered = ["feelings", "liked_ikos", "company", "areas", "budget", "nights"]
        .reduce((n, k) => n + (Array.isArray(pref[k]) ? (pref[k].length ? 1 : 0) : (pref[k] ? 1 : 0)), 0);
      setPrefsSet(user ? answered > 0 : null);
    }).finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() => {
    const list = events.filter((e) => iko === "all" || e.iko === iko);
    list.sort((a, b) => (nextStartAt(a) || "").localeCompare(nextStartAt(b) || ""));
    return list;
  }, [events, iko]);

  const [paid, unpaid] = useMemo(() => {
    const p = [], u = [];
    filtered.forEach((e) => (e.paid_placement ? p : u).push(e));
    return [p, u];
  }, [filtered]);

  // Featured slot: Wave 15.13a (Vania 5 Oct) · the pick must START in the
  // future / later today. An event that has already started is NEVER the
  // pick, even if it's still on (multi-week groups, retreats, etc.). We
  // compute `future` as "start_at >= now" and only sample from it.
  // Preference order inside `future`: is_day_pick happening TODAY → any
  // is_day_pick → the soonest overall. `rest` is NEVER restricted to
  // `future` so events happening now still appear in usikose / today.
  const [feature, rest] = useMemo(() => {
    if (!unpaid.length) return [null, []];
    const nowIso = new Date().toISOString();
    const future = unpaid.filter((e) => (e.start_at || "") >= nowIso);
    if (!future.length) return [null, unpaid];
    const now = new Date();
    const picks = future.filter((e) => e.is_day_pick);
    const pickToday = picks.find((e) => bucketEvent(nextStartAt(e), now) === "today");
    const chosen = pickToday || picks[0] || future[0];
    return [chosen, unpaid.filter((e) => e.id !== chosen.id)];
  }, [unpaid]);

  const now = new Date();

  // TODAY = all today's events except the featured one.
  const todayRows = useMemo(() =>
    rest.filter((e) => bucketEvent(nextStartAt(e), now, e.end_at) === "today"),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rest]);

  // USIKOSE (don't miss it) — up to 6 biggest upcoming events BEYOND today,
  // ranked by bigness (going_count / day_pick), then soonest, with an iko mix.
  const usikoseRows = useMemo(() => {
    const beyond = rest.filter((e) => {
      const b = bucketEvent(nextStartAt(e), now, e.end_at);
      return b && b !== "today";
    });
    beyond.sort((a, b) => {
      const bb = bigScore(b) - bigScore(a);
      if (bb !== 0) return bb;
      return (nextStartAt(a) || "").localeCompare(nextStartAt(b) || "");
    });
    return mixByIko(beyond, USIKOSE_LIMIT);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest]);

  // YOU'D PROBABLY LOVE THIS — always shown. Ranks by user's join answers.
  const yplt = useMemo(() => {
    const likedIkos = new Set(prefs.liked_ikos || []);
    const areas = new Set((prefs.areas || []).map((a) => a.toLowerCase()));
    const budget = prefs.budget || "";
    const pool = rest.slice();
    // score each event
    function score(e) {
      let s = 0;
      if (likedIkos.size && likedIkos.has(e.iko)) s += 5;
      const vd = (e.venue_display || "").toLowerCase();
      if (areas.size && !areas.has("anywhere")) {
        for (const a of areas) { if (a && vd.includes(a.replace(/_/g, " "))) { s += 3; break; } }
      }
      // budget rough parse
      const priceRaw = (e.price || "").toLowerCase();
      const isFree = !priceRaw || priceRaw === "free";
      const amount = (priceRaw.match(/(\d[\d,]*)/) || [null, "0"])[1].replace(/,/g, "");
      const num = parseInt(amount, 10) || 0;
      if (budget === "free" && isFree) s += 3;
      else if (budget === "under_1000" && (isFree || num < 1000)) s += 3;
      else if (budget === "1000_3000" && num >= 1000 && num <= 3000) s += 3;
      else if (budget === "3000_plus" && num > 3000) s += 3;
      // freshness — soonest gets a small bump
      const t = new Date(nextStartAt(e) || 0).getTime();
      const daysAhead = (t - Date.now()) / 864e5;
      if (daysAhead >= 0 && daysAhead <= 3) s += 1;
      return s;
    }
    pool.sort((a, b) => {
      const sd = score(b) - score(a);
      if (sd !== 0) return sd;
      return (nextStartAt(a) || "").localeCompare(nextStartAt(b) || "");
    });
    return mixByIko(pool, YPLT_LIMIT);
  }, [rest, prefs]);

  const filteredPast = useMemo(() =>
    pastEvents.filter((e) => iko === "all" || e.iko === iko),
  [pastEvents, iko]);

  const Card = band === "s" || band === "m" ? CompactCard : VerticalCard;
  // WW-173 · a chain shows once, as a chain card inside the section of its next event.
  // WW-212 · the pick slot is always a single event; a chain never takes it.
  const chains = useChains();
  const chainPlan = useMemo(() => {
    const pick = null;
    const secs = { usikose: usikoseRows, today: todayRows, ypl: yplt };
    const host = { usikose: [], today: [], ypl: [], been: [] };
    Object.values(chains).forEach((c) => {
      if (c.status === "ended") {
        if (filteredPast.some((e) => e.chain_id === c.id)) host.been.push(c);
        return;
      }
      if (pick && c.id === pick.id) return;
      const k = Object.keys(secs).find((s) => secs[s].some((e) => e.id === c.next_event_id))
        || Object.keys(secs).find((s) => secs[s].some((e) => e.chain_id === c.id))
        || (feature && c.next_event_id === feature.id ? "usikose" : null);
      if (k) host[k].push(c);
    });
    return { pick, host };
  }, [chains, feature, usikoseRows, todayRows, yplt, filteredPast]);
  // WW-173.a · once a chain card is on the page, its events stay out of every other section.
  const shownChainIds = useMemo(() => new Set([
    ...(chainPlan.pick ? [chainPlan.pick.id] : []),
    ...Object.values(chainPlan.host).flat().map((c) => c.id),
  ]), [chainPlan]);
  const own = (k, rows) => rows.filter((e) => !shownChainIds.has(e.chain_id));
  const chainName = (e) => (e.chain_id && chains[e.chain_id] ? chains[e.chain_id].name : "");
  const pickChainId = feature?.chain_id && chains[feature.chain_id] ? feature.chain_id : null;
  // WW-212 · when the pick is in a chain, its chain card starts below the first screen (measured).
  const [foldIdx, setFoldIdx] = useState(0);
  useEffect(() => {
    const reset = () => setFoldIdx(0);
    window.addEventListener("resize", reset);
    return () => window.removeEventListener("resize", reset);
  }, []);
  useLayoutEffect(() => {
    if (!pickChainId) return;
    const el = document.querySelector(`[data-testid="chain-card-${pickChainId}"]`);
    if (!el || !el.nextElementSibling) return;
    if (el.getBoundingClientRect().top + window.scrollY < window.innerHeight) setFoldIdx((i) => i + 1);
  });
  const renderCards = (k, rows) => {
    // WW-212 · a chain card sits where its next event falls by date; the section's own order is kept.
    const nextAt = (c) => (c.events.find((e) => e.id === c.next_event_id) || {}).start_at || "9999";
    const mine = own(k, rows);
    const nodes = mine.map((e) => <Card key={e.id} event={e} onOpenPoster={setViewer} chainName={chainName(e)} />);
    chainPlan.host[k].forEach((c) => {
      const at = k === "been" ? -1 : c.id === pickChainId ? Math.min(foldIdx, nodes.length)
        : mine.findIndex((e) => (nextStartAt(e) || "") > nextAt(c));
      nodes.splice(at < 0 ? nodes.length : at, 0, <ChainFeedCard key={`chain-${c.id}`} chain={c} onOpenPoster={setViewer} />);
    });
    return nodes;
  };
  const isDesktop = useIsDesktop();
  // Wave 9 · desktop grid: 4 cols ≥1280, 3 cols 1024-1279, phone keeps band.
  const useGridClass = isDesktop; // ww-grid-cards css covers 1 to 4 cols
  const desktopCols = (typeof window !== "undefined" && window.innerWidth >= 1280) ? 4
    : (typeof window !== "undefined" && window.innerWidth >= 1024) ? 3
    : cols;
  const gridStyle = useGridClass ? undefined : {
    display: "grid",
    gridTemplateColumns: `repeat(${desktopCols}, minmax(0, 1fr))`,
    gap: isDesktop ? 20 : (desktopCols === 1 ? 12 : 16),
  };
  const gridClassName = useGridClass ? "ww-grid-cards" : undefined;

  const pickLabel = feature ? pickPillLabel(nextStartAt(feature)) : "our pick";

  // Wave 15.12 (Vania 5 Oct) · scroll-edge fade. Anchored to the chip
  // row's immediate parent (position:relative) with `bottom: -22` so the
  // fade top sits flush with the chip row's bottom edge (0px gap) when
  // scrolled. Opacity binds to plain React state. Reading
  // `--ww-ground-fade` keeps the terminator in the ground colour at 0
  // alpha (never generic `transparent` which greys cream out).
  const fadeEl = (
    <div aria-hidden data-testid="feed-top-fade"
         data-visible={scrolled ? "1" : "0"}
         style={{ position: "absolute", left: 0, right: 0, bottom: -38,
                  height: 22, pointerEvents: "none", zIndex: 2,
                  opacity: scrolled ? 1 : 0,
                  transition: "opacity 180ms ease-out",
                  background: "var(--ww-ground-fade)" }} />
  );

  return (
    <div className="flex h-full flex-col" data-testid="feed-foryou"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      {/* Wave 12.16 · stories row above the tabs on both phone and desktop. */}
      {user && (
        <div className="flex-shrink-0" style={{ background: "var(--ww-ground)" }}>
          <div className="ww-container" style={{ paddingTop: 8 }}>
            <StoriesRowLite user={user} />
          </div>
        </div>
      )}
      {/* Msg 251 + Wave 9 · desktop filter bar under the stories row. */}
      {/* 12.20a + 12.20b + 14.4 (Vania 2 Oct) · phone rhythm reset + 12px
          clear space below the sticky pill row.
          · 8px stories→greeting
          · 16px greeting→tabs→chips
          · 16px (phone) / 20px (desktop) solid-ground padding BELOW the pills
            lives on the sticky filter block, not the scroll region, so the
            gap stays visible while scrolling and the content never touches
            the pills. */}
      {/* Wave C.15 (Vania 7 Oct) · desktop For You now gets 20px top padding
          so the sticky chip row never crushes against the topbar. Phone keeps
          its own rhythm (stories row carries the 8px above greeting). */}
      <div className="flex-shrink-0"
           style={{ background: "var(--ww-ground)",
                    borderBottom: isDesktop ? "1px solid var(--ww-line)" : "none",
                    paddingTop: isDesktop ? 20 : 0,
                    paddingBottom: isDesktop ? 20 : 16,
                    position: "relative" }}>
        <div className="ww-container">
          {/* Wave 15 Step 9 (Founder 2 Oct) · greeting moved into the phone
              header. Nothing renders here. */}
          {isDesktop ? (
            <div data-testid="foryou-filter-row"
                 style={{ paddingTop: 12, paddingBottom: 0,
                          position: "relative",
                          display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="ww-desktop-tab-row">
                <LovableTabs
                  testId="foryou-tabs"
                  tabs={[
                    { k: "events",  label: "events" },
                    { k: "posts",   label: "media" },
                    { k: "reviews", label: "reviews" },
                  ]}
                  active={tab}
                  onChange={setTab} />
              </div>
              <IkoChipRow active={iko} onSelect={setIko} keys={IKO_KEYS} />
              {fadeEl}
            </div>
          ) : (
            <>
              <LovableTabs
                testId="foryou-tabs"
                tabs={[
                  { k: "events",  label: "events" },
                  { k: "posts",   label: "media" },
                  { k: "reviews", label: "reviews" },
                ]}
                active={tab}
                onChange={setTab} />
              {tab === "events" && (
                <div style={{ marginTop: 16, position: "relative" }}>
                  <IkoChipRow active={iko} onSelect={setIko} keys={IKO_KEYS} />
                  {fadeEl}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-8"
           style={{ overscrollBehavior: "contain",
                    position: "relative" }}
           data-testid="feed-scroll-region">
        <div className="ww-container">
          {(tab === "posts" || tab === "reviews") && !isDesktop && (
            <div style={{ paddingTop: 8, marginBottom: 8 }}>
              <IkoChipRow active={iko} onSelect={setIko} keys={IKO_KEYS} />
            </div>
          )}
          {tab === "posts" && <div style={{ paddingTop: 4 }}><PostsGrid setViewer={setViewer} /></div>}
          {tab === "reviews" && <div style={{ paddingTop: 4 }}><ReviewsFeed /></div>}
          {tab === "events" && <>
          {loading && <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>loading…</p>}

          {/* Wave 12.3 + 12.15 · our pick section, upcoming-only, labelled.
              Phone: full-width wide feature card. Desktop: Option B (our pick
              on 2/3 + "also this weekend" / "coming up" on 1/3). */}
          {feature && (
            <OurPickBlock feature={feature} isDesktop={isDesktop} wide={wide}
                          allUpcoming={filtered.filter((e) => e.id === feature.id || !shownChainIds.has(e.chain_id))} pickLabel={pickLabel}
                          setViewer={setViewer} chainName={chainName(feature)} />
          )}

          {/* 2 · USIKOSE (don't miss it) — no card spans two columns. */}
          {usikoseRows.length > 0 && (
            <Section testId="section-usikose"
                     title="usikose"
                     subtitle="(don't miss it)"
                     gridStyle={gridStyle}
                     gridClassName={gridClassName}>
              {renderCards("usikose", usikoseRows)}
            </Section>
          )}

          {/* 3 · TODAY */}
          {todayRows.length > 0 && (
            <Section testId="section-today" title="today" gridStyle={gridStyle} gridClassName={gridClassName}>
              {renderCards("today", todayRows)}
            </Section>
          )}

          {/* 4 · YOU'D PROBABLY LOVE THIS */}
          {/* Msg 216 §1c — signed-in members without saved answers get a card
              directly above this rail. Hidden once answered or on guests. */}
          {user && prefsSet === false && (
            <button data-testid="tell-us-what-youre-into"
                    onClick={() => nav("/app/welcome")}
                    style={{
                      width: "100%", marginBottom: 12, background: "var(--ww-surface)",
                      border: "1px solid var(--ww-accent-ink)", borderRadius: 14,
                      padding: "14px 16px", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 12,
                      color: "var(--ww-text)", textAlign: "left",
                    }}>
              <span style={{ width: 36, height: 36, borderRadius: 999, background: "#FFD60A",
                              color: "#1A1A30", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
                <Sparkles size={18} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontFamily: "Poppins", fontWeight: 700, fontSize: 15, textTransform: "lowercase" }}>
                  tell us what you're into
                </span>
                <span style={{ display: "block", fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>
                  six quick taps and this rail becomes truly yours.
                </span>
              </span>
              <span style={{ color: "var(--ww-accent-ink)", fontFamily: "Poppins", fontSize: 14, fontWeight: 700 }}>start</span>
            </button>
          )}
          {yplt.length > 0 && (
            <Section testId="section-ypl"
                     title="you'd probably love this"
                     gridStyle={gridStyle} gridClassName={gridClassName}>
              {renderCards("ypl", yplt)}
            </Section>
          )}

          {/* 5 · PAID PARTNERSHIP */}
          {paid.length > 0 && (
            <PaidPartnershipRail rows={paid} setViewer={setViewer} />
          )}

          {/* 6 · INSIDERS' FAVOURITES (paid) or LOCKED TEASER (guests / free) */}
          {!loading && (isPaid ? (
            <InsidersFavouritesSection setViewer={setViewer} hideChainIds={shownChainIds} />
          ) : (
            <LockedFavouritesTeaser />
          ))}

          {/* WW-146.h · SAFARI (outside kenya), right before been there? */}
          {!loading && safariRows.filter((e) => iko === "all" || e.iko === iko).length > 0 && (
            <Section testId="section-safari"
                     title="safari"
                     subtitle="(outside kenya)"
                     gridStyle={gridStyle}
                     gridClassName={gridClassName}>
              {safariRows.filter((e) => iko === "all" || e.iko === iko).map((e) => <Card key={e.id} event={e} onOpenPoster={setViewer} />)}
            </Section>
          )}

          {/* 7 · BEEN THERE? */}
          {!loading && filteredPast.length > 0 && (
            <Section testId="section-been-there"
                     title="been there?"
                     subtitle="events from the last 3 months. tap i went to review."
                     subtitleAsParagraph
                     gridStyle={gridStyle} gridClassName={gridClassName}>
              {renderCards("been", filteredPast.slice(0, 12))}
            </Section>
          )}
          </>}
        </div>
      </div>

      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function Section({ title, subtitle, subtitleAsParagraph, testId, children, gridStyle, gridClassName }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <h2 className="mb-1 lowercase"
          data-testid={`section-heading-${testId?.replace("section-", "")}`}
          style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.015em",
                   fontSize: 28, lineHeight: 1.15, color: "var(--ww-text)" }}>
        {title}
        {subtitle && !subtitleAsParagraph && (
          <span style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 13,
                         color: "var(--ww-text-muted)", marginLeft: 10, letterSpacing: 0,
                         textTransform: "none" }}>
            {subtitle}
          </span>
        )}
      </h2>
      {subtitle && subtitleAsParagraph && (
        <p className="mb-3" style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>
          {subtitle}
        </p>
      )}
      {!subtitleAsParagraph && <div style={{ height: 12 }} />}
      <div className={gridClassName} style={gridStyle}>{children}</div>
    </section>
  );
}

function PaidPartnershipRail({ rows, setViewer }) {
  return (
    <section className="mb-8" data-testid="section-paid-partnership">
      <h2 data-testid="section-heading-paid-partnership"
          style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                   fontSize: 12, color: "var(--ww-text-muted)", marginBottom: 12 }}>
        paid partnership
      </h2>
      <div className="flex gap-3 overflow-x-auto pb-3" style={{ scrollbarWidth: "none" }}>
        {rows.map((e) => (
          <div key={e.id} style={{ flex: "0 0 auto", width: 260 }}>
            <CompactCard event={e} onOpenPoster={setViewer} />
          </div>
        ))}
      </div>
    </section>
  );
}

// WW-203 (option C) · most saved by insiders · one sideways row of the compact cards, edge to edge.
function InsidersFavouritesSection({ setViewer, hideChainIds }) {
  const [allRows, setRows] = useState([]);
  const rows = allRows.filter((e) => !hideChainIds.has(e.chain_id));
  const [pad, setPad] = useState(16);
  useEffect(() => {
    const measure = () => {
      const h = document.querySelector("[data-testid=section-heading-insiders-favourites]");
      if (h) setPad(Math.max(0, Math.round(h.getBoundingClientRect().left)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [rows.length]);
  useEffect(() => {
    publicClient.get("/public/feed/insiders-favourites?limit=10", { withCredentials: true })
      .then((r) => setRows(r.data.events || []))
      .catch(() => setRows([]));
  }, []);
  if (rows.length < 3) return null;
  return (
    <Section testId="section-insiders-favourites" title="insiders' favourites"
             subtitle="what insiders are saving right now. swipe for more." subtitleAsParagraph
             gridStyle={{ width: "100vw", marginLeft: -pad }}>
      <div data-testid="insiders-favourites-row" className="flex gap-3 overflow-x-auto pb-3"
           style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", paddingLeft: pad, paddingRight: 16, scrollPaddingLeft: pad }}>
        {rows.map((e) => (
          <div key={e.id} data-testid={`insiders-fav-${e.id}`} style={{ flex: "0 0 auto", width: 300, maxWidth: "82vw", scrollSnapAlign: "start" }}>
            <CompactCard event={e} onOpenPoster={setViewer} />
            <p data-testid={`insiders-fav-count-${e.id}`} className="mt-1 flex items-center gap-1 truncate"
               style={{ fontFamily: "Arial", fontSize: 12, fontWeight: 700, color: "var(--ww-text-muted)", whiteSpace: "nowrap" }}>
              <Bookmark size={12} fill="currentColor" aria-hidden="true" />
              saved by {e.saved_by_insiders} insider{e.saved_by_insiders === 1 ? "" : "s"}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function StoriesRowLite({ user }) {
  // Msg 234 §4 · lite version — only the "your story" tile. Real 24h stories,
  // other members' rings, etc. come later. Row uses a bottom hairline like the
  // Lovable bar so it sits above the tabs cleanly.
  const picture = user?.photo_url || user?.picture || "";
  const initial = ((user?.display_name || user?.name || user?.handle || "?") + "")[0].toUpperCase();
  return (
    <div data-testid="foryou-stories-row"
         style={{ display: "flex", gap: 14, padding: "6px 0 8px",
                  borderBottom: "1px solid rgba(245, 239, 230, 0.14)",
                  marginBottom: 0, overflowX: "auto", scrollbarWidth: "none" }}>
      <Link to="/app/new-story" data-testid="your-story-tile"
            style={{ display: "flex", flexDirection: "column", alignItems: "center",
                     gap: 6, textDecoration: "none", color: "inherit", flexShrink: 0 }}>
        <span style={{ position: "relative", width: 68, height: 68, borderRadius: 999,
                       padding: 3, background: "linear-gradient(135deg, #F5EFE6, #C9C9DA)",
                       display: "inline-grid", placeItems: "center" }}>
          {picture ? (
            <img src={picture} alt="your story"
                 style={{ width: 62, height: 62, borderRadius: 999, objectFit: "cover",
                          border: "2px solid #1A1A30", background: "var(--ww-surface)" }} />
          ) : (
            <span style={{ width: 62, height: 62, borderRadius: 999, background: "var(--ww-surface)",
                            color: "var(--ww-text)", border: "2px solid var(--ww-ground)",
                            display: "grid", placeItems: "center",
                            fontFamily: "Poppins", fontWeight: 800, fontSize: 22 }}>
              {initial}
            </span>
          )}
          <span aria-hidden="true"
                style={{ position: "absolute", right: 0, bottom: 0, width: 22, height: 22,
                          borderRadius: 999, background: "#FFD60A", color: "#1A1A30",
                          border: "2px solid #1A1A30",
                          display: "grid", placeItems: "center" }}>
            <Camera size={12} />
          </span>
        </span>
        <span className="lowercase"
              style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13,
                       color: "var(--ww-text)", whiteSpace: "nowrap" }}>
          your story
        </span>
      </Link>
    </div>
  );
}

function LockedFavouritesTeaser() {
  return (
    <section className="mb-8" data-testid="section-insiders-favourites-locked"
             style={{ background: "var(--ww-surface)", border: "1px solid var(--ww-line)", borderRadius: 16, padding: 20 }}>
      <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, color: "var(--ww-text-muted)" }}>
        <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999,
                         background: "var(--kili)", color: "var(--on-iko-fill)",
                         letterSpacing: "0.18em" }}>insiders only</span>
      </p>
      <h2 className="mt-1 mb-2 lowercase"
          style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 22 }}>
        insiders' favourites · become an insider to see
      </h2>
      <p style={{ fontFamily: "Arial", fontSize: 14, color: "var(--ww-text-muted)" }}>
        the events most marked going and saved by insiders this fortnight.
      </p>
      <Link to="/app/membership" data-testid="favourites-join-cta"
            style={{ display: "inline-block", marginTop: 12, padding: "10px 20px", borderRadius: 999,
                     background: "#FFD60A", color: "#1A1A30",
                     fontFamily: "Poppins", fontWeight: 700, textDecoration: "none", textTransform: "lowercase" }}>
        start free for 30 days
      </Link>
    </section>
  );
}

/* Wave 12.3 + 12.15 (Vania 1 Oct · Option B) · our pick section.
 * Phone keeps the current wide feature card.
 * Desktop ≥1024: our pick on 2/3 + 1/3 side card titled "also this weekend"
 * (Fri-Sun Nairobi) or "coming up" when fewer than 3 weekend rows. The
 * weekend side excludes the feature event itself. If no side rows at all,
 * our pick takes full width. Both halves share height. No nested links —
 * the whole card except the two buttons opens the event. */
function OurPickBlock({ feature, isDesktop, wide, allUpcoming, pickLabel, setViewer, chainName = "" }) {
  const nav = useNavigate();
  // Wave 15.13a · pick must start in the future / later today. ForYou's
  // selector already enforces this but the guard is kept defensively.
  const nowIso = new Date().toISOString();
  if (!feature || (feature.start_at || "") < nowIso) return null;

  if (!isDesktop) {
    return (
      <div className="mb-6" data-testid="our-pick-slot">
        <FeatureCard event={feature} onOpenPoster={setViewer} wide={wide} pillLabel={pickLabel} chainName={chainName} />
      </div>
    );
  }
  const weekend = pickWeekendRows(allUpcoming, feature);
  const useWeekendTitle = weekend.length >= 3;
  const sideTitle = useWeekendTitle ? "also this weekend" : "coming up";
  const sideRows = (weekend.length >= 3 ? weekend : allUpcoming.filter((e) => e.id !== feature.id)).slice(0, 3);

  if (sideRows.length === 0) {
    return (
      <div className="mb-8" data-testid="our-pick-slot">
        <OurPickCard ev={feature} pickLabel={pickLabel} chainName={chainName} onOpen={() => nav(`/app/event/${feature.id}`)} />
      </div>
    );
  }
  return (
    <div className="mb-8" data-testid="our-pick-slot"
         style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, alignItems: "stretch" }}>
      <OurPickCard ev={feature} pickLabel={pickLabel} chainName={chainName} onOpen={() => nav(`/app/event/${feature.id}`)} />
      <aside data-testid="our-pick-side"
             style={{ background: "var(--ww-surface)", border: "1px solid var(--ww-line)",
                      borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <h3 className="lowercase"
            style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.015em",
                     fontSize: 18, color: "var(--ww-text)", marginBottom: 10 }}>
          {sideTitle}
        </h3>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          {sideRows.map((e) => (
            <li key={e.id}>
              <button type="button" data-testid={`our-pick-side-${e.id}`}
                      onClick={() => nav(`/app/event/${e.id}`)}
                      style={{ width: "100%", textAlign: "left", background: "transparent",
                               border: 0, cursor: "pointer", padding: "10px 0",
                               borderBottom: "1px solid var(--ww-line)",
                               display: "flex", alignItems: "baseline", gap: 10, minHeight: 44 }}>
                <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                                textTransform: "lowercase", color: "var(--ww-text)", flex: "0 0 auto" }}>
                  {shortDayLabel(e)}
                </span>
                <span style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text)",
                                textTransform: "lowercase", flex: 1, minWidth: 0,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {(e.title || "").toLowerCase()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

function OurPickCard({ ev, pickLabel, onOpen, chainName = "" }) {
  const label = `${pickLabel.toLowerCase()} · ${ikoLabel(ev.iko).toLowerCase()}`;
  const price = formatPriceLine(ev);
  const parts = [metaLine(nextStartAt(ev), "", ev.end_at), ev.venue_display || "", price].filter(Boolean);
  return (
    <article data-testid="our-pick-card"
             onClick={onOpen}
             style={{ position: "relative", display: "flex", gap: 20, padding: 20,
                      background: ikoPanelBg(ev.iko),
                      border: "1px solid var(--ww-line)", borderRadius: 16,
                      overflow: "hidden", cursor: "pointer" }}>
      <div style={{ flex: "0 0 auto" }}>
        <img src={ev.cover_image_url || ""} alt=""
             style={{ width: 64, height: 86, objectFit: "contain", borderRadius: 5,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      background: "var(--ww-ground)" }} />
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                    fontSize: 11, color: "var(--ww-text-muted)" }}>
          {label}
        </p>
        <h2 className="lowercase"
            style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.02em",
                     fontSize: 26, lineHeight: 1.15, color: ikoTitleVar(ev.iko),
                     margin: "4px 0 6px",
                     display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {(ev.title || "").toLowerCase()}
        </h2>
        <p style={{ fontFamily: "Arial", fontSize: 13, lineHeight: 1.4, color: "var(--ww-text)" }}>
          {parts.join(" · ")}
        </p>
        <PartOfLine ev={ev} chainName={chainName} />
        <div className="mt-auto flex items-center gap-10"
             style={{ paddingTop: 14 }}
             onClick={(e) => e.stopPropagation()}>
          {ev.ticket_link ? (
            <a href={ev.ticket_link} target="_blank" rel="noreferrer"
               data-testid="our-pick-tickets"
               onClick={(e) => {
                 e.stopPropagation();
                 trackTicketClick({
                   eventId: ev.id,
                   organiserHandle: ev.organiser_handle || "",
                   source: "card",
                 });
               }}
               style={{ padding: "10px 16px", borderRadius: 999,
                        background: "#FFD60A", color: "#1A1A30",
                        fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                        textTransform: "lowercase", textDecoration: "none",
                        display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44 }}>
              <Ticket size={14} /> get tickets
            </a>
          ) : null}
          <Link to={`/app/event/${ev.id}`} data-testid="our-pick-going"
                style={{ padding: "10px 16px", borderRadius: 999,
                         background: "transparent", color: "var(--ww-text)",
                         border: "1px solid var(--ww-line-strong)",
                         fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                         textTransform: "lowercase", textDecoration: "none",
                         display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44 }}>
            <Check size={14} /> i'm going
          </Link>
        </div>
      </div>
    </article>
  );
}

function pickWeekendRows(allUpcoming, feature) {
  const now = new Date();
  const nairobi = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Nairobi" }));
  const dow = nairobi.getDay();
  const daysToFri = (5 - dow + 7) % 7;
  const friStart = new Date(nairobi); friStart.setDate(friStart.getDate() + daysToFri); friStart.setHours(0, 0, 0, 0);
  const sunEnd = new Date(friStart); sunEnd.setDate(sunEnd.getDate() + 3); sunEnd.setHours(0, 0, 0, 0);
  const inWindow = (e) => {
    const s = new Date(nextStartAt(e) || e.start_at || "");
    return !isNaN(s) && s >= friStart && s < sunEnd;
  };
  return allUpcoming.filter((e) => e.id !== feature.id && inWindow(e));
}

function shortDayLabel(e) {
  // Wave 15.13a · a multi-week event that has already started must never
  // flash its old start weekday on compact lists. Return "now" so the
  // side rail reads e.g. "now · mizizi space · 8-week group support".
  if (isOnNow(e.start_at, e.end_at)) return "now";
  const s = new Date(nextStartAt(e) || e.start_at || "");
  if (isNaN(s)) return "";
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Nairobi", weekday: "short" })
    .format(s).toLowerCase();
}
